import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Base CRUD service for the NestJS REST API.
 *
 * Each feature service passes its REST resource path (e.g. 'customers',
 * 'work-orders') and inherits standard REST operations. Unlike the previous
 * ABP backend, the API returns plain JSON (objects/arrays) with no
 * `{ result, success }` envelope, so responses are used directly.
 *
 * Conventions on the API:
 *   GET    /api/{resource}        -> list (array)
 *   GET    /api/{resource}/:id    -> single
 *   POST   /api/{resource}        -> create
 *   PUT    /api/{resource}/:id    -> update (id taken from the payload)
 *   DELETE /api/{resource}/:id    -> delete
 */
@Injectable({
  providedIn: 'root',
})
export class BaseService {
  /** Full URL for this resource, e.g. https://host/api/customers */
  protected resourceUrl: string;

  constructor(
    @Inject(String) protected resource: string,
    protected http: HttpClient
  ) {
    // environment.apiUrl ends with `/api/`
    this.resourceUrl = environment.apiUrl + resource;
  }

  get<T>(id: number): Observable<T> {
    return this.http.get<T>(`${this.resourceUrl}/${id}`).pipe(share());
  }

  getAll<T>(): Observable<T> {
    return this.http.get<T>(this.resourceUrl).pipe(share());
  }

  create<T>(data: any): Observable<T> {
    return this.http.post<T>(this.resourceUrl, data).pipe(share());
  }

  update<T>(data: any): Observable<T> {
    return this.http
      .put<T>(`${this.resourceUrl}/${data.id}`, data)
      .pipe(share());
  }

  delete<T>(id: number): Observable<T> {
    return this.http.delete<T>(`${this.resourceUrl}/${id}`).pipe(share());
  }
}
